import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../../../software/api";
import { X, Loader2, FileSearch, Truck, ClipboardList, Wrench, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dmy = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "");

const FORMS = {
  quotations: { label: "Spas/Gazebos/Saunas Quotations", icon: FileSearch, cols: [["client", "Client"], ["quote_number", "Quote #"], ["quote_date", "Quotation Date", dmy], ["salesperson", "Sales Person"], ["amount", "Amount", money]] },
  waiting_delivery: { label: "Spas/Gazebos/Saunas Waiting Delivery", icon: Truck, cols: [["client", "Client"], ["invoice_number", "Invoice #"], ["quote_date", "Quotation Date", dmy], ["sale_date", "Date Sold", dmy], ["delivery_date", "Delivery Date", dmy], ["salesperson", "Sales Person"], ["balance_due", "Balance Due", money]] },
  wo_quotations: { label: "Active Work Order Quotations", icon: ClipboardList, cols: [["client", "Client"], ["suburb", "Suburb"], ["wo_number", "Work Order #"], ["date_booked", "Date Booked", dmy], ["work_type", "Work Type"], ["taken_by", "Taken By"]] },
  wo_in_progress: { label: "Work Orders In Progress", icon: Wrench, cols: [["client", "Client"], ["suburb", "Suburb"], ["wo_number", "Work Order #"], ["date_booked", "Date Booked", dmy], ["work_type", "Work Type"], ["carried_out_by", "Carried Out By"]] },
};

const StatusForm = ({ kind, onClose, onOpenClient }) => {
  const f = FORMS[kind];
  const [range, setRange] = useState({ start: "", end: "" });
  const [d, setD] = useState(null);
  const [sort, setSort] = useState(null); // {key, dir: 1 | -1}
  const sortBy = (k) => setSort((p) => (p?.key === k ? { key: k, dir: -p.dir } : { key: k, dir: 1 }));
  const rows = !d ? [] : !sort ? d.rows : [...d.rows].sort((a, b) => {
    const x = a[sort.key], y = b[sort.key];
    if (x == null || x === "") return 1;
    if (y == null || y === "") return -1;
    const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "en-AU", { numeric: true, sensitivity: "base" });
    return c * sort.dir;
  });
  useEffect(() => {
    setD(null);
    const qs = new URLSearchParams(Object.entries(range).filter(([, v]) => v)).toString();
    api("GET", `/status-search/${kind}${qs ? `?${qs}` : ""}`).then((r) => { setD(r); if (!range.start && r.start) setRange({ start: r.start, end: r.end }); });
  }, [kind, range.start, range.end]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="fixed inset-0 z-[95] bg-black/50 flex items-center justify-center p-3" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid={`status-form-${kind}`}>
      <div className="w-full max-w-5xl h-[86vh] flex flex-col bg-[#f4f1e6] border-2 border-[#8a6d16] rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#ffcc66] border-b-2 border-[#8a6d16] px-4 py-2 flex flex-wrap items-center gap-3">
          <span className="font-display font-black text-lg text-[#123a63]" data-testid="status-form-title">{d?.title || f.label}</span>
          {d?.range_label && (
            <span className="flex items-center gap-1.5 text-[12px] font-bold text-[#123a63]">
              {d.range_label}:
              <input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="status-form-start" className="bg-[#ffffcc] border border-[#7f9db9] px-1 py-0.5 text-[12px]" />
              And:
              <input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="status-form-end" className="bg-[#ffffcc] border border-[#7f9db9] px-1 py-0.5 text-[12px]" />
            </span>
          )}
          <span className="ml-auto text-[12px] font-bold text-[#123a63]" data-testid="status-form-count">{d ? `${d.rows.length} records` : ""}</span>
          <button onClick={onClose} data-testid="status-form-close" className="bg-white border border-[#8a6d16] px-3 py-1 text-[12px] font-bold text-[#123a63] hover:bg-[#fff6dd]">Close Form</button>
        </div>
        <div className="flex-1 overflow-auto bg-white">
          {!d ? <div className="py-16 grid place-items-center text-[#1a4f8a]"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 bg-[#d6dfec] text-[#123a63]"><tr>{f.cols.map(([k, l]) => (
                <th key={k} className="text-left px-0 py-0 font-black border-b border-[#5b8ac4]">
                  <button onClick={() => sortBy(k)} data-testid={`status-form-sort-${k}`} title="Click to sort A–Z, click again for Z–A"
                    className={`w-full flex items-center gap-1 px-2 py-1.5 text-left hover:bg-[#c3d1e6] ${sort?.key === k ? "bg-[#c3d1e6]" : ""}`}>
                    {l}{sort?.key === k ? (sort.dir === 1 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                  </button>
                </th>
              ))}</tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} onClick={() => r.client_id && onOpenClient(r.client_id, r.tab)} data-testid="status-form-row"
                    className={`border-b border-slate-100 ${r.client_id ? "cursor-pointer hover:bg-[#eaf1fb]" : "opacity-60"}`}>
                    {f.cols.map(([k, , fmt]) => <td key={k} className="px-2 py-1">{fmt ? fmt(r[k]) : r[k] ?? ""}</td>)}
                  </tr>
                ))}
                {d.rows.length === 0 && <tr><td colSpan={f.cols.length} className="py-10 text-center text-slate-400">No records.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default function StatusSearches({ onClose, onOpenClient }) {
  const [open, setOpen] = useState(null);
  return createPortal(
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="status-searches-popup">
      <div className="w-full max-w-md bg-[#f4f1e6] border-2 border-[#8a6d16] rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#ffcc66] border-b-2 border-[#8a6d16] px-4 py-2 flex items-center justify-between">
          <span className="font-display font-black text-[#123a63]">STATUS SEARCHES</span>
          <button onClick={onClose} data-testid="status-searches-close"><X className="h-5 w-5 text-[#123a63]" /></button>
        </div>
        <div className="p-4 grid gap-2.5">
          {Object.entries(FORMS).map(([k, f]) => (
            <button key={k} onClick={() => setOpen(k)} data-testid={`status-search-btn-${k}`}
              className="flex items-center gap-3 bg-white border-2 border-[#5b8ac4] rounded-sm px-4 py-3 text-left font-bold text-[#123a63] hover:bg-[#eaf1fb] hover:-translate-y-px transition-[transform,background-color]">
              <f.icon className="h-5 w-5 shrink-0" />{f.label}
            </button>
          ))}
        </div>
      </div>
      {open && <StatusForm kind={open} onClose={() => setOpen(null)} onOpenClient={(id, tab) => { setOpen(null); onOpenClient(id, tab); }} />}
    </div>,
    document.body
  );
}
